import { observer } from 'mobx-react-lite'
import { useGapDialogStore } from 'reusable/gap-dialog/GapDialogStore'
import ArrowRightAltOutlined from '@mui/icons-material/ArrowRightAltOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import GapAutocompleteSingle from 'features/gap-components/autocomplete/single/GapAutocompleteSingle'
import useStore from 'reusable/mobx-store-utils'
import CreateClientCSVImportFieldsStore, { CreateClientCSVImportFieldsStoreProvider, useCreateClientCSVImportFieldsStore } from './CreateClientCSVImportFieldsStore'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import useInitialMount from 'reusable/hooks/useInitialMount'
import { themeConfig } from '@gap/shared/theme'

export default observer(function CreateClientCSVImportFields() {
   const gapDialogStore = useGapDialogStore()

   const createClientCSVImportFieldsStore = useStore(sp => new CreateClientCSVImportFieldsStore(sp), { gapDialogStore })

   useInitialMount(() => {
      createClientCSVImportFieldsStore.prefetchClientNames()
   })

   if (createClientCSVImportFieldsStore.dataError) {
      return <Typography variant="body1">{createClientCSVImportFieldsStore.dataError}</Typography>
   }

   return <CreateClientCSVImportFieldsStoreProvider store={createClientCSVImportFieldsStore}>
      {createClientCSVImportFieldsStore.step === 'review' ? <ReviewRows /> : <MapColumns />}
   </CreateClientCSVImportFieldsStoreProvider>
})

const MapColumns = observer(function MapColumns() {
   const createClientCSVImportFieldsStore = useCreateClientCSVImportFieldsStore()

   return <Stack sx={{ gap: 2 }}>
      {createClientCSVImportFieldsStore.data.fieldNames.map(fieldName => (
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} key={fieldName}>
            <TextField variant="outlined" label={fieldName} value={createClientCSVImportFieldsStore.rows[0]?.[fieldName]} disabled sx={{ width: '50%' }} />
            <ArrowRightAltOutlined />
            <Dropdown fieldName={fieldName as keyof CreateClientCSVImportFieldsStore['fields']} />
         </Box>
      ))}

      {createClientCSVImportFieldsStore.errorMessage && <Typography variant="body1" sx={{ color: 'error.main' }}>{createClientCSVImportFieldsStore.errorMessage}</Typography>}
   </Stack>
})

const ReviewRows = observer(function ReviewRows() {
   const createClientCSVImportFieldsStore = useCreateClientCSVImportFieldsStore()

   return <Stack sx={{ gap: 2 }}>
      {createClientCSVImportFieldsStore.reviewGroups.map(group => (
         <Stack key={group.lineNumber} sx={{ gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
               <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
                  <Typography component="span" variant="body1Bold">Line {group.lineNumber}</Typography>
                  {` (${group.description})`}
               </Typography>
               <Button size="small" variant="text" data-line-number={group.lineNumber} onClick={createClientCSVImportFieldsStore.removeReviewRowButtonHandler}>
                  Remove row
               </Button>
            </Box>
            {group.cells.map(cell => (
               <Box key={cell.column} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: cell.isResolved ? themeConfig.primary[600] : 'error.main' }}>
                     {cell.message}
                  </Typography>
                  <TextField
                     label={cell.label}
                     value={cell.value}
                     onChange={createClientCSVImportFieldsStore.reviewFieldChangeHandler}
                     slotProps={{ htmlInput: { 'data-line-number': group.lineNumber, 'data-column': cell.column } }}
                     sx={{ width: 300, flexShrink: 0 }}
                  />
               </Box>
            ))}
         </Stack>
      ))}
   </Stack>
})

const Dropdown = observer(function Dropdown(props: { fieldName: keyof CreateClientCSVImportFieldsStore['fields'] }) {
   const createClientCSVImportFieldsStore = useCreateClientCSVImportFieldsStore()

   return createClientCSVImportFieldsStore.formHandler[props.fieldName].bindInput(ip => (
      <GapAutocompleteSingle
         options={createClientCSVImportFieldsStore.dropdownOptions}
         label=""
         {...ip}
         sx={{ width: '50%' }}
      />
   ))
})
